import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { DnsRounded } from '@mui/icons-material';
import {
  Button, FormControlLabel, FormGroup, MenuItem, Switch,
} from '@mui/material';
import { useRequest } from 'shared/hooks';
import { vtrAudit } from 'shared/api/networking';
import { getUnitPath } from 'shared/routes/building';
import ModalTable from 'shared/components/table/ModalTable';
import { VARIANTS } from 'shared/constants';

const columns = [
  {
    name: 'Unit',
    formatter: (row) => <Link to={getUnitPath(row.building_id, row.unit_id)}>{row.unit_name}</Link>,
  },
  {
    name: 'VTR',
    jsonKey: 'vtr',
  },
  {
    name: 'Vlan',
    jsonKey: 'vlan',
  },
  {
    name: 'QOS',
    jsonKey: 'qos_description',
  },
  {
    name: 'Expected speed',
    jsonKey: 'expected_speed',
  },
  {
    name: 'Actual speed',
    jsonKey: 'actual_speed',
  },
  {
    name: 'Status',
    formatter: (row) => (row.ok ? 'OK' : row.error || 'mismatch'),
  },
];

/**
 * @param {string} queryKey - Query key for audit filter (nas, ip, unit_id, or building_id)
 * @param {string|number} queryValue - Value for the query key
 * @param {boolean} [asButton=false] - Whether to render as a button instead of menu item
 * @param {boolean} [disabled=false] - Whether the component is disabled
 */
export default function VTRAudit({
  asButton = false,
  disabled = false,
  queryKey,
  queryValue,
}) {
  const [message, setMessage] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [show, setShow] = useState(false);
  const [auditResult, setAuditResult] = useState([]);
  const { request } = useRequest();

  useEffect(() => {
    (async () => {
      if (!show) return;
      const urlParams = new URLSearchParams();
      urlParams.set(queryKey, queryValue);
      urlParams.set('filter_ok', !showAll);
      const result = await request(vtrAudit(`?${urlParams.toString()}`));
      if (result === undefined) return;

      if (result && result.data) {
        setAuditResult(result.data);
        setMessage('');
      } else {
        if (result && result.message) {
          setMessage(result.message);
        } else {
          setMessage('Unable to complete VTR audit');
        }
        setAuditResult([]);
      }
    })();
  }, [request, queryKey, queryValue, show, showAll]);

  let vtrAuditInitializer;
  if (asButton) {
    vtrAuditInitializer = (
      <Button
        disabled={disabled}
        variant={VARIANTS.outlined}
        startIcon={<DnsRounded />}
        onClick={() => setShow(true)}
      >
        VTR Audit
      </Button>
    );
  } else {
    vtrAuditInitializer = (
      <MenuItem disabled={disabled} key="vtr-audit" onClick={() => setShow(true)}>
        <DnsRounded />
        &nbsp;
        VTR Audit
      </MenuItem>
    );
  }
  return (
    <>
      <ModalTable
        columns={columns}
        noRowsText={message}
        rowId="vlan"
        rows={auditResult}
        setShow={setShow}
        show={show}
        title="VTR Audit"
      >
        <FormGroup>
          <FormControlLabel
            control={(
              <Switch
                checked={showAll}
                id="edit-toggle-switch"
                onChange={() => setShowAll(!showAll)}
                slotProps={{ input: { 'aria-label': 'controlled' } }}
              />
            )}
            label="Show all"
          />
        </FormGroup>
      </ModalTable>
      {vtrAuditInitializer}
    </>
  );
}
